import { contentTemplates, type delivery } from '@bb/contracts';
import type { TemplateChannel, TemplateContent, TemplateInstance, TemplateSource } from '../../api/client.ts';

/**
 * Pure helpers of the template library (BB-56): which delivery channels a template channel fills, what a composer
 * saves as a template, and how an inserted template (a copy plus its source) lands in a campaign variant or a Message
 * step. The message keeps the copy; later template versions never change it.
 */
type DeliveryChannel = delivery.DeliveryChannel;
type ChannelContent = Record<string, unknown>;

export const TEMPLATE_CHANNELS = contentTemplates.TEMPLATE_CHANNELS;

export const TEMPLATE_CHANNEL_LABELS: Record<TemplateChannel, string> = { push: 'Push', card: 'Content Card', iam: 'In-app message', banner: 'Banner', webhook: 'Webhook', canvas: 'Canvas' };

/** The content keys of a channel (`push_android`/`push_ios` for push, `in_app` for IAM, …). */
export const templateSlots = (channel: TemplateChannel): readonly string[] => contentTemplates.TEMPLATE_SLOTS[channel];

/** The template channel a composer's delivery channel saves to and inserts from (`null`: none, e.g. web push). */
export function templateChannelOf(channel: DeliveryChannel): TemplateChannel | null {
  if (channel === 'push_android' || channel === 'push_ios') return 'push';
  if (channel === 'content_card') return 'card';
  if (channel === 'in_app') return 'iam';
  if (channel === 'webhook') return 'webhook';
  return null;
}

/** What «Save as template» stores from a message's channel contents: the slots of the channel that have content. */
export function templateContentOf(channel: TemplateChannel, content: Partial<Record<string, ChannelContent>>): TemplateContent {
  return Object.fromEntries(templateSlots(channel).flatMap((slot) => (content[slot] && Object.keys(content[slot]).length ? [[slot, structuredClone(content[slot])]] : [])));
}

/** An empty content for a new template of the channel (push starts with both platforms). */
export function emptyTemplateContent(channel: TemplateChannel): TemplateContent {
  if (channel === 'push') return { push_android: {}, push_ios: {} };
  if (channel === 'card') return { content_card: { card: { type: 'classic', action: { kind: 'none' }, pinned: false, dismissible: true, extras: {} }, creationMode: 'first_impression', expiry: { mode: 'duration', seconds: 30 * 86_400 } } };
  if (channel === 'iam' || channel === 'banner') return { [templateSlots(channel)[0]!]: { pages: [{ rows: [] }] } };
  if (channel === 'canvas') return { definition: { schemaVersion: 1, variants: [], steps: {} } };
  return { webhook: {} };
}

/** A message (campaign variant or Message step) with channel contents and their template sources. */
export interface TemplateTarget {
  channels: DeliveryChannel[];
  content: Partial<Record<DeliveryChannel, ChannelContent>>;
  sourceTemplates?: Partial<Record<DeliveryChannel, TemplateSource>>;
}

/**
 * Inserts a template into a message: each channel the message has and the template fills gets a copy of the content and
 * records the source; the message's channels stay as they are. `null` when the template fills none of them (a push
 * template for another platform).
 */
export function insertTemplate<T extends TemplateTarget>(target: T, instance: TemplateInstance): T | null {
  const filled = templateSlots(instance.channel).filter((s): s is DeliveryChannel => target.channels.includes(s as DeliveryChannel) && Boolean(instance.content[s]));
  if (filled.length === 0) return null;
  const content = { ...target.content };
  const sources = { ...target.sourceTemplates };
  for (const slot of filled) {
    content[slot] = structuredClone(instance.content[slot]!);
    sources[slot] = instance.source;
  }
  return { ...target, content, sourceTemplates: sources };
}

/** Drops the sources of channels the message no longer has. */
export function pruneSources(sources: Partial<Record<DeliveryChannel, TemplateSource>> | undefined, channels: readonly DeliveryChannel[]): Partial<Record<DeliveryChannel, TemplateSource>> | undefined {
  if (!sources) return undefined;
  const kept = Object.fromEntries(Object.entries(sources).filter(([c]) => channels.includes(c as DeliveryChannel)));
  return Object.keys(kept).length ? kept : undefined;
}
